import { CommonModule, NgClass } from '@angular/common';
import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FloatingAssistanceButtonComponent } from '../../../../shared/components/floating-assistance-button/floating-assistance-button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicesService } from '../../services/services.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationModalComponent } from '../../../../shared/components/notification-modal/notification-modal.component';

@Component({
  selector: 'app-services',
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    MatIconModule,
    FloatingAssistanceButtonComponent
  ],
  templateUrl: './services.component.html',
  styleUrl: './services.component.scss'
})
export class ServicesComponent implements OnInit, OnDestroy {
  tabs: any[] = []
  selectedTab = signal('Todos');
  allServices: any[] = [];
  loadingImages = signal(true);
  searchQuery = signal('');

  otrosServiciosMedicos = [
    { nombre: 'Honorarios médicos por COVID-19', uso: '2/4 gratis' },
    { nombre: 'Renta de Equipo Médico', uso: '3/4 gratis' },
    { nombre: 'Enfermeria a Domicilio', uso: '1/4 gratis' },
    { nombre: 'Segunda Opinión', uso: '1/4 gratis' },
    { nombre: 'Referencias Médicas', uso: '2/4 gratis' },
    { nombre: 'Check-up Médico', uso: '3/4 gratis' },
  ];

  constructor(
    private api: ServicesService,
    private router: Router,
    private loader: LoaderService,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.allServicesClient();
    this.listenToSearchEvents();
  }

  get filteredServices() {
    const current = this.selectedTab();
    const query = this.searchQuery().toLowerCase();

    let services = current === 'Todos'
      ? this.allServices
      : this.allServices.filter(s => s.tipo === current);

    if (query) {
      services = services.filter(s =>
        s.nombre.toLowerCase().includes(query) ||
        s.tipo.toLowerCase().includes(query)
      );
    }

    return services;
  }

  get servicesByTipo() {
    const query = this.searchQuery().toLowerCase();

    return this.tabs
      .filter(tab => tab.label !== 'Todos')
      .map(tab => {
        let servicios = this.allServices.filter(s => s.tipo === tab.label);

        if (query) {
          servicios = servicios.filter(s =>
            s.nombre.toLowerCase().includes(query) ||
            s.tipo.toLowerCase().includes(query)
          );
        }

        return {
          tipo: tab.label,
          icon: tab.icon,
          servicios
        };
      })
      .filter(group => group.servicios.length > 0);
  }

  allServicesClient() {
    this.loader.show();
    this.api.getServicesPopular().subscribe({
      next: (res) => {
        if (res?.data) {
          this.allServices = res.data.map((item: any) => ({
            tipo: this.mapTipo(item.dsServicio),
            nombre: item.dsSubServicio === 'CONSULTA MÉDICA' ? 'CONSULTA MÉDICA EN CONSULTORIO' : item.dsSubServicio,
            uso: this.formatUsage(item.Disponibles, item.Tomados),
            imagen: item.img,
            clServicio: item.clServicio,
            clSubServicio: item.clSubServicio,
            dsServicio: item.dsServicio,
            dsSubServicio: item.dsSubServicio,
            Tomados: item.Tomados,
            Disponibles: item.Disponibles
          }));

          const tiposUnicos = [...new Set(this.allServices.map(s => s.tipo))];
          this.tabs = [
            { label: 'Todos', icon: 'search' },
            ...tiposUnicos.map(tipo => ({
              label: tipo,
              icon: this.iconForTipo(tipo)
            }))
          ];
          this.filterByParams();
          this.loader.hide();
        }
      },
      error: (err) => console.error('Error al obtener servicios', err)
    });
  }

  private iconForTipo(tipo: string) {
    switch (tipo) {
      case 'Médica': return 'medical_services';
      case 'Vial': return 'directions_car';
      case 'Hogar': return 'home';
      case 'Mascotas': return 'pets';
      case 'Legal': return 'balance';
      default: return 'help_outline';
    }
  }

  selectTab(tab: string) {
    this.selectedTab.set(tab);
  }

  private mapTipo(dsServicio: string): string {
    return dsServicio.replace('Asistencia ', '').trim();
  }

  goToService(service: any) {
    const tomados = Number(service.Tomados);
    const disponibles = service.Disponibles === '00' ? Infinity : Number(service.Disponibles);

    if (tomados >= disponibles && disponibles !== Infinity) {
      this.dialog.open(NotificationModalComponent, {
        data: {
          type: 'info',
          title: 'Has alcanzado el límite de servicios disponibles.',
          message: 'Si tienes servicios en curso, puedes consultar su detalle en el apartado "<b>Solicitudes</b>".'
        }
      });
      return;
    }

    const solicitudData = {
      servicio: {
        clServicio: service.clServicio,
        clSubservicio: service.clSubServicio,
        dsServicio: service.dsServicio,
        dsSubServicio: service.dsSubServicio
      }
    };
    localStorage.setItem('solicitudData', JSON.stringify(solicitudData));

    this.router.navigate(
      ['/service', service.clSubServicio],
      { state: service }
    );
  }

  goToBeneficios() {
    this.router.navigate(['/beneficios']);
  }

  filterByParams(){
    this.route.queryParams.subscribe((params)=>{
      if(params && params['services']){
        this.selectTab(params['services']);
      }
      if(params && params['search']){
        this.searchQuery.set(params['search']);
      }
    });
  }

  formatUsage(tomados: string, disponibles: string): string {
    const formatValue = (value: string) => value === '00' ? '∞' : value;
    return `${formatValue(tomados)}/${formatValue(disponibles)}`;
  }

  private listenToSearchEvents() {
    // Escuchar eventos de búsqueda desde el header
    window.addEventListener('headerSearch', (event: any) => {
      this.searchQuery.set(event.detail || '');
    });
  }

  ngOnDestroy() {
    // Limpiar event listeners
    window.removeEventListener('headerSearch', this.listenToSearchEvents);
  }

  onSearch(query: string) {
    this.searchQuery.set(query);
  }
}
